import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { getTrelloBoard } from "@/lib/trello.functions";
import type { TrelloBoardData } from "@/lib/trello-types";
import { AlertTriangle, ExternalLink, PauseCircle, RefreshCw } from "lucide-react";

export const Route = createFileRoute("/trello")({
  head: () => ({
    meta: [
      { title: "Trello-pipeline — Inkubera" },
      {
        name: "description",
        content: "Live-spegling av Inkuberas Trello-board för månadsrapportering.",
      },
    ],
  }),
  loader: () => getTrelloBoard(),
  component: TrelloPage,
});

const INTERVAL_OPTIONS = [
  { label: "Av", value: 0 },
  { label: "1 min", value: 60_000 },
  { label: "2 min", value: 120_000 },
  { label: "5 min", value: 300_000 },
];

const MAX_CONSECUTIVE_FAILURES = 3;

function TrelloPage() {
  const initial = Route.useLoaderData() as {
    data: TrelloBoardData | null;
    error: string | null;
  };

  const [data, setData] = useState(initial.data);
  const [error, setError] = useState(initial.error);
  const [intervalMs, setIntervalMs] = useState<number>(120_000);
  const [refreshing, setRefreshing] = useState(false);
  const [nextIn, setNextIn] = useState<number>(intervalMs / 1000);
  const [failures, setFailures] = useState(0);
  const [autoPaused, setAutoPaused] = useState(false);
  const [lastSuccess, setLastSuccess] = useState<string | null>(
    initial.data?.fetchedAt ?? null,
  );
  const didInitToast = useRef(false);

  // Surface initial loader error once
  useEffect(() => {
    if (!didInitToast.current && initial.error) {
      toast.error("Trello-synk misslyckades", { description: initial.error });
      didInitToast.current = true;
    }
  }, [initial.error]);

  async function refresh(manual = false) {
    setRefreshing(true);
    try {
      const res = await getTrelloBoard();
      if (res.error || !res.data) {
        const msg = res.error ?? "Okänt fel.";
        setError(msg);
        setFailures((f) => {
          const next = f + 1;
          if (manual) {
            toast.error("Kunde inte synka Trello", { description: msg });
          } else if (next >= MAX_CONSECUTIVE_FAILURES && !autoPaused) {
            setAutoPaused(true);
            toast.error("Auto-uppdatering pausad", {
              description: `Misslyckades ${next} gånger i rad.`,
            });
          }
          return next;
        });
      } else {
        setData(res.data);
        setError(null);
        setFailures(0);
        setAutoPaused(false);
        setLastSuccess(res.data.fetchedAt);
        if (manual) toast.success("Trello synkad");
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Nätverksfel";
      setError(`Kunde inte nå servern: ${msg}`);
      if (manual) toast.error("Synk misslyckades", { description: msg });
    } finally {
      setRefreshing(false);
      setNextIn(Math.floor(intervalMs / 1000));
    }
  }

  useEffect(() => {
    if (!intervalMs || autoPaused) return;
    setNextIn(Math.floor(intervalMs / 1000));
    const id = setInterval(() => refresh(false), intervalMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, autoPaused]);

  useEffect(() => {
    if (!intervalMs || autoPaused) return;
    const id = setInterval(() => {
      setNextIn((s) => (s <= 1 ? Math.floor(intervalMs / 1000) : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [intervalMs, autoPaused]);

  const stale = lastSuccess && error;

  return (
    <AppShell>
      <section className="px-8 pt-10 pb-6 border-b border-border">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-2">
              Live från Trello
            </div>
            <h1 className="font-display text-4xl">
              {data?.boardName ?? "Trello-pipeline"}
            </h1>
            <p className="text-muted-foreground mt-2 max-w-2xl">
              Speglar månadsrapporteringen direkt från Trello.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <div className="flex items-center gap-1 p-1 rounded-lg bg-secondary border border-border">
              <span className="text-xs text-muted-foreground px-2">Auto:</span>
              {INTERVAL_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setIntervalMs(opt.value);
                    setAutoPaused(false);
                  }}
                  className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                    intervalMs === opt.value
                      ? "bg-foreground text-background font-medium"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {data?.boardUrl && (
              <a
                href={data.boardUrl}
                target="_blank"
                rel="noreferrer"
                className="h-9 px-3 rounded-lg border border-border bg-card text-sm flex items-center gap-2 hover:bg-secondary"
              >
                <ExternalLink className="size-4" /> Öppna i Trello
              </a>
            )}
            <button
              onClick={() => refresh(true)}
              disabled={refreshing}
              className="h-9 px-3 rounded-lg bg-foreground text-background text-sm font-medium flex items-center gap-2 hover:opacity-90 disabled:opacity-50"
            >
              <RefreshCw className={`size-4 ${refreshing ? "animate-spin" : ""}`} />
              Uppdatera
            </button>
          </div>
        </div>
        <div className="text-xs text-muted-foreground mt-3 flex items-center gap-3 flex-wrap">
          {lastSuccess && (
            <span>
              Senast lyckad synk {new Date(lastSuccess).toLocaleTimeString("sv-SE")}
            </span>
          )}
          {intervalMs > 0 && !autoPaused && !error && (
            <span className="flex items-center gap-1.5">
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Nästa uppdatering om {nextIn}s
            </span>
          )}
          {autoPaused && (
            <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
              <PauseCircle className="size-3.5" />
              Auto-uppdatering pausad efter {failures} misslyckade försök
            </span>
          )}
          {failures > 0 && !autoPaused && (
            <span className="text-amber-600 dark:text-amber-400">
              {failures} misslyckat försök i rad
            </span>
          )}
        </div>
      </section>

      {error && (
        <div className="mx-8 mt-6 p-4 rounded-xl border border-destructive/40 bg-destructive/10 flex items-start gap-3">
          <AlertTriangle className="size-5 text-destructive shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm text-destructive">
              Trello-synk misslyckades
            </div>
            <div className="text-sm text-foreground/80 mt-1 break-words">{error}</div>
            {stale && (
              <div className="text-xs text-muted-foreground mt-2">
                Visar senast kända data från {new Date(lastSuccess!).toLocaleString("sv-SE")}.
              </div>
            )}
          </div>
          <button
            onClick={() => refresh(true)}
            disabled={refreshing}
            className="h-8 px-3 rounded-md bg-destructive text-destructive-foreground text-xs font-medium flex items-center gap-1.5 hover:opacity-90 disabled:opacity-50 shrink-0"
          >
            <RefreshCw className={`size-3.5 ${refreshing ? "animate-spin" : ""}`} />
            Försök igen
          </button>
        </div>
      )}

      {!data && !error && (
        <div className="mx-8 mt-6 p-8 text-center text-muted-foreground text-sm">
          Hämtar data från Trello…
        </div>
      )}

      {data && (
        <section className="p-8 overflow-x-auto">
          <div className="flex gap-4 min-w-max pb-4">
            {data.lists.map((list) => {
              const cards = data.cards.filter((c) => c.idList === list.id);
              return (
                <div key={list.id} className="w-80 shrink-0">
                  <div className="flex items-center justify-between mb-3 px-1">
                    <span className="font-medium">{list.name}</span>
                    <span className="text-xs text-muted-foreground">{cards.length}</span>
                  </div>
                  <div className="rounded-2xl p-2.5 space-y-2 min-h-32 border border-dashed border-border bg-card/40">
                    {cards.map((card) => (
                      <a
                        key={card.id}
                        href={card.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block p-3 rounded-xl bg-card border border-border hover:border-foreground/40 hover:shadow-md transition-all"
                      >
                        <div className="font-medium text-sm leading-tight">{card.name}</div>
                        {card.labels.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {card.labels.map((l) => (
                              <span
                                key={l.id}
                                className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground"
                              >
                                {l.name || l.color || "label"}
                              </span>
                            ))}
                          </div>
                        )}
                        {card.desc && (
                          <p className="text-xs text-muted-foreground mt-2 line-clamp-3 whitespace-pre-line">
                            {card.desc}
                          </p>
                        )}
                        {card.due && (
                          <div className="text-[11px] text-muted-foreground mt-2">
                            Deadline: {new Date(card.due).toLocaleDateString("sv-SE")}
                          </div>
                        )}
                      </a>
                    ))}
                    {cards.length === 0 && (
                      <div className="text-xs text-muted-foreground/60 text-center py-6">
                        Tomt
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </AppShell>
  );
}
